import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  LocalHospital as ProcedureIcon,
  MonetizationOn as CostIcon,
  Timer as DurationIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import Axios from "axios";
import useUserState from "../../hooks/useUserState";
import useDentalAccess from "../../hooks/useDentalAccess";
import useBDocTranslator from "../../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2, 0),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
  },
  table: {
    minWidth: 650,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    minWidth: 500,
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  costCell: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
  },
  durationCell: {
    display: "flex",
    alignItems: "center",
  },
  categoryChip: {
    fontWeight: "bold",
  },
  activeChip: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  inactiveChip: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    minWidth: 120,
    textAlign: "center",
  },
}));

const PROCEDURE_CATEGORIES = {
  preventive: { label: "Preventivo", color: "primary" },
  restorative: { label: "Restaurativo", color: "secondary" },
  surgical: { label: "Quirúrgico", color: "error" },
  orthodontic: { label: "Ortodoncia", color: "info" },
  endodontic: { label: "Endodoncia", color: "warning" },
  periodontic: { label: "Periodoncia", color: "success" },
  prosthetic: { label: "Protésico", color: "default" },
  cosmetic: { label: "Cosmético", color: "secondary" },
};

function DentalProcedures({ isAdminView = false }) {
  const classes = useStyles();
  const [userState] = useUserState();
  const dentalAccess = useDentalAccess();
  const { t } = useBDocTranslator();

  // Estados principales
  const [procedures, setProcedures] = useState([]);
  const [filteredProcedures, setFilteredProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState(true);

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    estimated_cost: "",
    estimated_duration: "",
    is_active: true,
    notes: "",
  });

  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    byCategory: {},
    avgCost: 0,
  });

  useEffect(() => {
    if (dentalAccess.canView) {
      fetchProcedures();
    }
  }, [dentalAccess.canView]);

  useEffect(() => {
    filterProcedures();
    calculateStats();
  }, [procedures, searchTerm, categoryFilter, activeFilter]);

  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/procedures`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      setProcedures(response.data || []);
    } catch (error) {
      console.error("Error fetching procedures:", error);
      setProcedures([]);
    } finally {
      setLoading(false);
    }
  };

  const filterProcedures = () => {
    let filtered = procedures;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (procedure) =>
          procedure.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          procedure.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por categoría
    if (categoryFilter) {
      filtered = filtered.filter(
        (procedure) => procedure.category === categoryFilter
      );
    }

    // Filtrar por estado activo
    filtered = filtered.filter((procedure) =>
      activeFilter ? procedure.is_active : !procedure.is_active
    );

    setFilteredProcedures(filtered);
  };

  const calculateStats = () => {
    const total = procedures.length;
    const active = procedures.filter((p) => p.is_active).length;

    const byCategory = {};
    procedures.forEach((p) => {
      byCategory[p.category] = (byCategory[p.category] || 0) + 1;
    });

    const costs = procedures
      .filter((p) => p.estimated_cost && p.estimated_cost > 0)
      .map((p) => p.estimated_cost);
    const avgCost =
      costs.length > 0 ? costs.reduce((a, b) => a + b, 0) / costs.length : 0;

    setStats({ total, active, byCategory, avgCost });
  };

  const handleOpenDialog = (procedure = null) => {
    if (procedure) {
      setSelectedProcedure(procedure);
      setFormData({
        name: procedure.name || "",
        description: procedure.description || "",
        category: procedure.category || "",
        estimated_cost: procedure.estimated_cost || "",
        estimated_duration: procedure.estimated_duration || "",
        is_active: procedure.is_active ?? true,
        notes: procedure.notes || "",
      });
    } else {
      setSelectedProcedure(null);
      setFormData({
        name: "",
        description: "",
        category: "",
        estimated_cost: "",
        estimated_duration: "",
        is_active: true,
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProcedure(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      estimated_cost: "",
      estimated_duration: "",
      is_active: true,
      notes: "",
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        estimated_cost: formData.estimated_cost
          ? parseFloat(formData.estimated_cost)
          : null,
        estimated_duration: formData.estimated_duration
          ? parseInt(formData.estimated_duration)
          : null,
      };

      if (selectedProcedure) {
        // Actualizar
        await Axios.put(
          `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/procedures/${selectedProcedure.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${userState?.jwt}`,
            },
          }
        );
      } else {
        // Crear
        await Axios.post(
          `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/procedures`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${userState?.jwt}`,
            },
          }
        );
      }

      handleCloseDialog();
      fetchProcedures();
    } catch (error) {
      console.error("Error saving procedure:", error);
      // TODO: Mostrar mensaje de error al usuario
    }
  };

  const handleDelete = async (procedureId) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/procedures/${procedureId}`,
        {
          headers: {
            Authorization: `Bearer ${userState?.jwt}`,
          },
        }
      );
      fetchProcedures();
    } catch (error) {
      console.error("Error deleting procedure:", error);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "No especificado";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "No especificado";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (!dentalAccess.canView) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="error">
            {dentalAccess.errorMessage}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent className={classes.loadingContainer}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.root}>
      <CardContent>
        <div className={classes.header}>
          <Typography variant="h6" component="div">
            <ProcedureIcon
              style={{ marginRight: 8, verticalAlign: "middle" }}
            />
            {t("Procedimientos Dentales")}
          </Typography>
          {(dentalAccess.canCreate || dentalAccess.isAdmin) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t("Nuevo Procedimiento")}
            </Button>
          )}
        </div>

        {/* Estadísticas */}
        <div className={classes.statsContainer}>
          <div className={classes.statCard}>
            <Typography variant="h6" color="primary">
              {stats.total}
            </Typography>
            <Typography variant="caption">
              {t("Total Procedimientos")}
            </Typography>
          </div>
          <div className={classes.statCard}>
            <Typography variant="h6" color="secondary">
              {stats.active}
            </Typography>
            <Typography variant="caption">{t("Activos")}</Typography>
          </div>
          <div className={classes.statCard}>
            <Typography variant="h6" color="textPrimary">
              {formatCurrency(stats.avgCost)}
            </Typography>
            <Typography variant="caption">{t("Costo Promedio")}</Typography>
          </div>
        </div>

        {/* Filtros */}
        <div className={classes.searchContainer}>
          <TextField
            placeholder={t("Buscar procedimientos...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            style={{ minWidth: 300 }}
          />

          <FormControl size="small" style={{ minWidth: 150 }}>
            <InputLabel>{t("Categoría")}</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("Todas")}</em>
              </MenuItem>
              {Object.entries(PROCEDURE_CATEGORIES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={activeFilter}
                onChange={(e) => setActiveFilter(e.target.checked)}
                color="primary"
              />
            }
            label={t("Solo Activos")}
          />
        </div>

        {filteredProcedures.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">
              {searchTerm || categoryFilter
                ? t(
                    "No se encontraron procedimientos con los filtros aplicados"
                  )
                : t("No hay procedimientos registrados")}
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Nombre")}</TableCell>
                  <TableCell>{t("Categoría")}</TableCell>
                  <TableCell>{t("Costo Estimado")}</TableCell>
                  <TableCell>{t("Duración")}</TableCell>
                  <TableCell>{t("Estado")}</TableCell>
                  <TableCell>{t("Acciones")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProcedures.map((procedure) => (
                  <TableRow key={procedure.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: "bold" }}
                      >
                        {procedure.name}
                      </Typography>
                      {procedure.description && (
                        <Typography variant="caption" color="textSecondary">
                          {procedure.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          PROCEDURE_CATEGORIES[procedure.category]?.label ||
                          procedure.category
                        }
                        color={
                          PROCEDURE_CATEGORIES[procedure.category]?.color ||
                          "default"
                        }
                        size="small"
                        className={classes.categoryChip}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={classes.costCell}>
                        <CostIcon style={{ marginRight: 4, fontSize: 16 }} />
                        {formatCurrency(procedure.estimated_cost)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={classes.durationCell}>
                        <DurationIcon
                          style={{ marginRight: 4, fontSize: 16 }}
                        />
                        {formatDuration(procedure.estimated_duration)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          procedure.is_active ? t("Activo") : t("Inactivo")
                        }
                        size="small"
                        className={
                          procedure.is_active
                            ? classes.activeChip
                            : classes.inactiveChip
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className={classes.actionButtons}>
                        {(dentalAccess.canEdit || dentalAccess.isAdmin) && (
                          <Tooltip title={t("Editar")}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(procedure)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(dentalAccess.canDelete || dentalAccess.isAdmin) && (
                          <Tooltip title={t("Eliminar")}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDelete(procedure.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Dialog para crear/editar procedimiento */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProcedure
            ? t("Editar Procedimiento")
            : t("Nuevo Procedimiento")}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label={t("Nombre del Procedimiento")}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className={classes.formRow}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth className={classes.formRow}>
                <InputLabel>{t("Categoría")}</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  required
                >
                  {Object.entries(PROCEDURE_CATEGORIES).map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Descripción")}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={classes.formRow}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("Costo Estimado")}
                value={formData.estimated_cost}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_cost: e.target.value })
                }
                className={classes.formRow}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("Duración Estimada (minutos)")}
                value={formData.estimated_duration}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    estimated_duration: e.target.value,
                  })
                }
                className={classes.formRow}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("Notas Adicionales")}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className={classes.formRow}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label={t("Procedimiento Activo")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="default">
            {t("Cancelar")}
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            {selectedProcedure ? t("Actualizar") : t("Crear")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DentalProcedures;
